// Event structure from server
export interface RealtimeEvent<T = any> {
	event: string
	payload: T
	timestamp: string
	id?: string
}

// Handler definition - now generic
export interface RealtimeEventHandler<T = any> {
	id: string
	eventType: string
	priority?: number // Lower number = higher priority
	condition?: (payload: T, event: RealtimeEvent<T>) => boolean
	handler: (payload: T, event: RealtimeEvent<T>) => Promise<void> | void
	once?: boolean // Execute only once
	componentId?: string // For React component cleanup
}

// Connection status - now generic
export enum ConnectionStatus {
	DISCONNECTED = "disconnected",
	CONNECTING = "connecting",
	CONNECTED = "connected",
	RECONNECTING = "reconnecting",
	ERROR = "error",
}

// Connection configuration - now generic
export interface RealtimeConnectionConfig {
	autoConnect?: boolean
	reconnectInterval?: number
	maxReconnectAttempts?: number
	heartbeatInterval?: number
	maxReconnectInterval?: number
}

// Event types enum - 规范命名为 aaa:bbb 格式
export enum RealtimeEventType {
	// User related events
	USER_LOGOUT = "user:logout",
	USER_BANNED = "user:banned",
	USER_DEACTIVATED = "user:deactivated",
	USER_PROFILE_UPDATED = "user:profile_updated",

	// System events
	SYSTEM_MAINTENANCE = "system:maintenance",
	SYSTEM_NOTIFICATION = "system:notification",

	// Business events
	ORDER_STATUS_CHANGED = "order:status_changed",
	PRODUCT_STOCK_UPDATED = "product:stock_updated",

	// Custom test events
	TEST_EVENT = "test:event",
	ORDER_CREATE_FINISHED = "order:create_finished",
}

// Built-in event payloads
export interface UserLogoutPayload {
	userId: string
	reason: string
	redirectUrl?: string
}

export interface UserBannedPayload {
	userId: string
	reason: string
	banType: "temporary" | "permanent"
	expiresAt?: string
}

export interface UserDeactivatedPayload {
	userId: string
	reason: string
	redirectUrl?: string
}

export interface SystemMaintenancePayload {
	startTime: string
	estimatedDuration: number
	message: string
}

export interface SystemNotificationPayload {
	title: string
	message: string
	type: "info" | "warning" | "error" | "success"
	actions?: Array<{
		label: string
		action: string
	}>
}

// Manager event types - now generic
export interface RealtimeManagerEvents {
	"connection-status-changed": ConnectionStatus
	"event-received": RealtimeEvent
	"handler-registered": RealtimeEventHandler
	"handler-unregistered": string
	error: Error
}
